import { createRouter, createWebHistory } from 'vue-router'
import Payment from '../views/Payment.vue'
import CheckoutPage from "../views/CheckoutPage.vue";
import VerifyPayment from '../views/VerifyPayment.vue'
import PaymentSuccess from '../views/PaymentSuccess.vue'
import OnboardingPage from "../views/OnboardingPage.vue";
import { useUserStore } from '@/store/user'
import { sendEvent } from '@/helpers/cerebro'
import { useOnboardingStore } from '@/store/onboarding'
import { getOnboardingFlowNameFromRoute } from '@/helpers/page'
import { QUIZ_IDS } from '@/constants/onboarding/quizIds'

const getRouteOptions = (to, from) => {
  const quizId = getOnboardingFlowNameFromRoute(from) || getOnboardingFlowNameFromRoute(to) || getOnboardingFlowNameFromRoute(to.redirectedFrom) || QUIZ_IDS.default
  const options = {
    query: { ...to.query },
    params: { ...to.params },
  }

  if (to.name === "OnboardingPage" || to.path?.includes("onboarding")) {
    options.params.oid = quizId ? `-${quizId}` : ''
    delete options.query.oid;
  } else {
    options.query.oid = quizId
  }
  
  return options
}

const routes = [
  { path: '/', redirect: { name: 'OnboardingPage' } },
  {
    path: '/onboarding:oid(.*)?',
    component: OnboardingPage,
    name: 'OnboardingPage',
    meta: { onlyUnauth: true },
  },
  // {
  //   path: '/verify',
  //   component: VerifyEmail,
  //   name: 'VerifyEmail',
  //   meta: { onlyAuth: true, onlyFree: true },
  // },
  // {
  //   path: '/settings',
  //   name: 'Settings',
  //   component: Settings,
  //   meta: { onlyAuth: true },
  // },
  {
    path: '/payment',
    name: 'Payment',
    component: Payment,
    meta: { onlyAuth: true, onlyFree: true },
  },
  {
    path: "/checkout/:clientSecret/:subscription?/:customer?",
    component: CheckoutPage,
    name: "Checkout",
    meta: { onlyAuth: true, onlyFree: true },
  },
  {
    path: '/verify-payment',
    component: VerifyPayment,
    name: 'VerifyPayment',
    meta: { onlyAuth: true },
  },
  // {
  //   path: '/payment-result',
  //   component: PaymentResult,
  //   name: 'PaymentResult',
  //   meta: { onlyAuth: true, onlyPro: true },
  // },
  {
    path: '/payment-success',
    component: PaymentSuccess,
    name: 'PaymentSuccess',
    meta: { onlyAuth: true, onlyPro: true },
  },
  { path: '/:pathMatch(.*)*', redirect: { name: 'OnboardingPage' } },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

const redirectPagePro = {
  name: "PaymentSuccess",
};
const redirectPageFree = {
  name: "Payment",
};
const redirectPageUnauth = {
  name: "OnboardingPage",
};

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const currentUser = await userStore.getCurrentUser()
  
  const routeOptions = getRouteOptions(to, from)
  const isQuizIdParamsChanged = routeOptions.query.oid !== to.query.oid || routeOptions.params.oid !== to.params.oid

  // If quiz ID params changed, redirect with updated params
  if (isQuizIdParamsChanged) {
    return next({ ...to, ...routeOptions })
  }

  // Handle authentication required routes
  if (to.meta.onlyAuth) {
    if (!currentUser) {
      return next({ ...redirectPageUnauth, ...routeOptions })
    }

    // Handle pro-only routes
    if (to.meta.onlyPro && !userStore.isUserPro()) {
      return next({ ...redirectPageFree, ...routeOptions })
    }

    // Handle free-only routes
    if (to.meta.onlyFree && userStore.isUserPro()) {
      return next({ ...redirectPagePro, ...routeOptions })
    }
  }

  // Handle unauthenticated-only routes
  if (to.meta.onlyUnauth) {
    if (currentUser) {
      return next({ ...redirectPageFree, ...routeOptions })
    }
    return next()
  }

  // Default case
  next()
})

router.afterEach((to, from, failure) => {
  window.scrollTo(0, 0)
  if (!failure) {
    const onboardingStore = useOnboardingStore()
    const { QUIZ_ID } = onboardingStore
    sendEvent({
      eventName: 'web_page_open',
      page: to.name,
      properties: {
        source: from.name || router.options.history.state.source || '',
        path: to.path,
        fullPath: to.fullPath,
        page_language: 'en',
        quiz_id: QUIZ_ID,
      },
    })
  }
})

export default router
